/**
 * Manual mat count badges on structure markers (AST-89, AST-111)
 *
 * One HTML marker (react-map-gl <Marker>, i.e. mapboxgl.Marker with a custom
 * element) per structure with a non-zero manual count, plus one for the
 * project-level count: a pill with the count only, dark bold text, a thin
 * outline in the same colour (AST-100 -- Mark: "change the line colour on
 * the green pills to the same green as the pill itself"), offset up-right
 * with a short leader line so it never covers the icon.
 * Clicking (or Enter/Space) opens a read-only popover: manual count by mat
 * type and the total.
 *
 * AST-111 (Mark): "love the amber pill idea". Green means mats are here;
 * amber means a net negative -- mats owed, or moved out and not yet
 * accounted for. Before this, negatives drew no pill at all, so a structure
 * at -25 looked identical on the map to one that never had mats, while See
 * Mat Locations showed the -25. Zero still draws nothing (AST-99).
 *
 * The project-level pill sits beside the project's own marker, not its
 * boundary -- Mark's call. See locationCounts.structureBadges(projectPoint).
 *
 * Decisions (no answer from Mark yet; recorded in the PR):
 * - Offset: always up-and-right. The spec's "away from the right-of-way
 *   line" needs a line to measure against, which we don't store. A fixed
 *   offset still keeps the icon clickable.
 * - Beacon "near this structure" in the popover is left out until Mark
 *   says what "near" means. The popover says beacon mats aren't included.
 *
 * Must be rendered as a child of react-map-gl's <Map>.
 */

import React, { useState } from 'react';
import { Marker, Popup } from 'react-map-gl';
import { Box, Typography } from '@mui/material';

const PILL_GREEN = '#4CAF50'; // colorPalette.success -- the app's mat green
// AST-111: canary/warning.main. Its contrastText is black, so the same dark
// pill text stays readable on amber as on green.
const PILL_AMBER = '#ECAE36';

// AST-113 (Mark): "Change the color of the text on the popups on the manual
// mat counts green pills to black".
//
// Mapbox renders .mapboxgl-popup-content on a WHITE background, but this app's
// theme is dark -- so `color: 'text.primary'` resolved to #E8DCDA (near-white)
// and the popup text was near-invisible. Same light-on-light trap as AST-99's
// header row. These are fixed dark values, not theme tokens, because the
// surface they sit on is mapbox's white and does not follow our palette.
const POPUP_TEXT = '#1A1A1A';
const POPUP_TEXT_MUTED = '#5A5A5A';
// warning.main (#ECAE36) is unreadable on white; use a dark amber that still
// reads as a warning.
const POPUP_TEXT_NEGATIVE = '#8A5A00';

const Pill = ({ badge, onOpen }) => {
  const colour = badge.negative ? PILL_AMBER : PILL_GREEN;
  // Screen readers get the sign and the reason, not a bare "-25".
  const label = badge.negative
    ? `${badge.name}: ${Math.abs(badge.total)} manual mats moved out or owed`
    : `${badge.name}: ${badge.total} manual mats`;
  return (
  <Box
    sx={{ position: 'relative', width: 0, height: 0, pointerEvents: 'none' }}
  >
    {/* Leader line from the marker up-right to the pill */}
    <Box
      aria-hidden
      sx={{
        position: 'absolute', left: 0, top: -14, width: 14, height: 2,
        bgcolor: colour, transform: 'rotate(-45deg)', transformOrigin: '0 100%',
        boxShadow: '0 0 1px rgba(0,0,0,0.6)',
      }}
    />
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onOpen(badge); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onOpen(badge); }
      }}
      sx={{
        position: 'absolute', left: 10, top: -30,
        pointerEvents: 'auto', cursor: 'pointer',
        minWidth: 22, height: 20, px: 0.75,
        // AST-111 follow-up: the project-level pill now sits among the
        // structure pills (it's anchored to the elements' centre so it's
        // actually on screen), so a bare number would read as just another
        // structure count. A dashed edge says "not a specific location"
        // without introducing a new colour -- the colour language stays
        // green/amber as Mark approved.
        border: badge.projectLevel ? `2px dashed ${colour}` : `1.5px solid ${colour}`,
        borderRadius: 10,
        bgcolor: colour, color: '#0B1F0C',
        font: '700 12px/17px Roboto, Arial, sans-serif',
        whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
        '&:focus-visible': { outline: '2px solid #FFFFFF', outlineOffset: 2 },
      }}
    >
      {badge.total.toLocaleString()}
    </Box>
  </Box>
  );
};

const StructureCountBadges = ({ badges = [] }) => {
  const [open, setOpen] = useState(null);
  // If the open badge's structure drops to 0 (or the layer changes), close.
  const current = open && badges.find((b) => b.id === open.id);

  return (
    <>
      {badges.map((b) => (
        <Marker key={b.id} longitude={b.position[0]} latitude={b.position[1]} anchor="center">
          <Pill badge={b} onOpen={setOpen} />
        </Marker>
      ))}
      {current && (
        <Popup
          longitude={current.position[0]}
          latitude={current.position[1]}
          anchor="bottom"
          offset={[18, -34]}
          closeOnClick
          onClose={() => setOpen(null)}
          maxWidth="260px"
        >
          <Box sx={{ minWidth: 170, color: POPUP_TEXT }} role="dialog" aria-label={`${current.name} manual mats`}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{current.name}</Typography>
            {current.byType.map((t) => (
              <Box key={t.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="body2">{t.name}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.n.toLocaleString()}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'rgba(0,0,0,0.2)' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{current.total.toLocaleString()}</Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: POPUP_TEXT_MUTED }}>
              {current.projectLevel
                ? 'Counted on the project, not at a mapped location.'
                : 'Manual counts only. Beacon mats aren\u2019t included.'}
            </Typography>
            {current.negative && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: POPUP_TEXT_NEGATIVE, fontWeight: 700 }}>
                Negative: more mats have left than arrived here.
              </Typography>
            )}
          </Box>
        </Popup>
      )}
    </>
  );
};

export default StructureCountBadges;
